const COOKIE_NAME = 'cookielawinfo-checkbox-marketing';
const POLL_MS = 2000;

/**
 * Reads document.cookie for cookielawinfo-checkbox-marketing.
 * Returns true only on the exact string "yes" (the GDPR Cookie Consent plugin's value).
 */
export function readConsent(): boolean {
  const cookies = document.cookie.split(';');
  for (const c of cookies) {
    const [k, v] = c.trim().split('=');
    if (k === COOKIE_NAME) return v === 'yes';
  }
  return false;
}

/**
 * Subscribe to consent changes. The callback fires only on TRANSITIONS
 * (false → true or true → false), never on no-op ticks.
 *
 * Returns an unsubscribe function.
 */
export function subscribeConsent(cb: (granted: boolean) => void): () => void {
  let last = readConsent();

  const tick = () => {
    const now = readConsent();
    if (now !== last) {
      last = now;
      cb(now);
    }
  };

  const onCustom = () => tick();
  const onStorage = (e: StorageEvent) => {
    if (e.key && e.key.toLowerCase().includes('consent')) tick();
  };
  const onVisibility = () => {
    if (document.visibilityState === 'visible') tick();
  };

  window.addEventListener('cookieyes_consent_update', onCustom);
  window.addEventListener('CookieLawInfoConsentChange', onCustom);
  window.addEventListener('storage', onStorage);
  document.addEventListener('visibilitychange', onVisibility);

  const intervalId = window.setInterval(tick, POLL_MS);

  return () => {
    window.removeEventListener('cookieyes_consent_update', onCustom);
    window.removeEventListener('CookieLawInfoConsentChange', onCustom);
    window.removeEventListener('storage', onStorage);
    document.removeEventListener('visibilitychange', onVisibility);
    window.clearInterval(intervalId);
  };
}

/**
 * Best-effort re-open of the GDPR Cookie Consent plugin's settings dialog.
 * Function names differ between plugin forks/versions — try the known ones.
 * Returns true if one was found and invoked.
 */
export function openCookieSettings(): boolean {
  const candidates = [
    'revisitCkyConsent', // CookieYes recent
    'CookieLawInfo_BarConfig_ReConsent', // WebToffee older fork
    'wt_cli_revisit_consent', // WebToffee newer
  ];
  for (const name of candidates) {
    const fn = (window as unknown as Record<string, unknown>)[name];
    if (typeof fn === 'function') {
      try {
        (fn as () => void)();
        return true;
      } catch {
        /* fall through */
      }
    }
  }
  // Some forks attach the dialog opener to a button with these classes —
  // synthesizing a click is the documented escape hatch.
  const btn = document.querySelector<HTMLElement>(
    '.cli-settings-button, .wt-cli-cookie-bar-container .cli_settings_button, .cky-btn-revisit',
  );
  if (btn) {
    btn.click();
    return true;
  }
  return false;
}
